import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { CheckIcon, Trash01Icon } from '@untitledui-icons/react/line'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { FormInput, FormSwitch, FormCombobox } from '@/components/forms'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useCustomer, useUpdateCustomer, useDeleteCustomer } from '@/hooks/api/useCustomers'
import { useSearchPartners, usePartner } from '@/hooks/api/usePartners'
import { usePermissions } from '@/hooks/api'

// Validation schema
const customerEditSchema = z.object({
  name: z.string().min(1, 'app.validation.name_required'),
  lookup_key: z.string().optional(),
  partner_id: z.number().nullable().optional(),
  allowed_to_make_templates: z.boolean(),
})

type CustomerEditFormValues = z.infer<typeof customerEditSchema>

export function CustomerEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [partnerSearch, setPartnerSearch] = useState('')
  const { isAdmin } = usePermissions()

  const customerId = parseInt(id || '0', 10)

  // Fetch customer data
  const { data: customer, isLoading: customerLoading } = useCustomer(customerId)

  // Fetch partners for autocomplete (admin only)
  const { data: partners = [] } = useSearchPartners(partnerSearch, isAdmin)

  // Fetch current partner to show its name when page loads
  const { data: currentPartner } = usePartner(
    customer?.partner_id ?? 0,
    { enabled: isAdmin && !!customer?.partner_id }
  )

  // Mutations
  const updateCustomer = useUpdateCustomer()
  const deleteCustomer = useDeleteCustomer()

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<CustomerEditFormValues>({
    resolver: zodResolver(customerEditSchema),
    defaultValues: {
      name: '',
      lookup_key: '',
      partner_id: null,
      allowed_to_make_templates: false,
    },
  })

  // Reset form when customer data loads
  useEffect(() => {
    if (customer) {
      reset({
        name: customer.name,
        lookup_key: customer.lookup_key || '',
        partner_id: customer.partner_id || null,
        allowed_to_make_templates: customer.meta?.allowed_to_make_templates || false,
      })
    }
  }, [customer, reset])

  // Partner options for combobox
  const partnerOptions = useMemo(() => {
    const options = partners.map((p) => ({
      value: p.id,
      label: p.name,
    }))
    // Ensure current partner is always in the options list
    if (currentPartner && !options.some((opt) => opt.value === currentPartner.id)) {
      options.unshift({ value: currentPartner.id, label: currentPartner.name })
    }
    return options
  }, [partners, currentPartner])

  // Handle form submission
  const onSubmit = async (data: CustomerEditFormValues) => {
    try {
      await updateCustomer.mutateAsync({
        id: customerId,
        name: data.name,
        lookup_key: data.lookup_key || undefined,
        partner_id: data.partner_id || undefined,
        meta: {
          allowed_to_make_templates: data.allowed_to_make_templates,
        },
      })
      toast.success(t('resources.customers.update_success'))
      navigate('/customers')
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.customers.update_error')
      toast.error(message)
    }
  }

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteCustomer.mutateAsync(customerId)
      toast.success(t('resources.customers.delete_success'))
      navigate('/customers')
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.customers.delete_error')
      toast.error(message)
    }
  }

  if (customerLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!customer) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">{t('resources.customers.not_found')}</p>
        <Button color="secondary" onPress={() => navigate('/customers')} className="mt-4">
          {t('resources.customers.back_to_customers')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('resources.customers.edit_title', { name: customer.name })}
        subtitle={t('resources.customers.edit_subtitle')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <FormInput
              control={control}
              name="name"
              label={t('resources.customers.fields.name')}
              placeholder={t('resources.customers.placeholders.name')}
              isRequired
            />

            <FormInput
              control={control}
              name="lookup_key"
              label={t('resources.customers.fields.lookup_key')}
              placeholder={t('resources.customers.placeholders.lookup_key')}
              description={t('resources.customers.descriptions.lookup_key')}
            />
          </div>

          {/* Partner (admin only) */}
          {isAdmin && (
            <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
              <FormCombobox
                control={control}
                name="partner_id"
                label={t('resources.customers.fields.partner_id')}
                options={partnerOptions}
                placeholder={t('resources.customers.placeholders.partner')}
                onSearchChange={setPartnerSearch}
                isRequired
              />
            </div>
          )}

          {/* Settings */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <FormSwitch
              control={control}
              name="allowed_to_make_templates"
              label={t('resources.customers.fields.allowed_to_make_templates')}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-gray-200 pt-6 dark:border-gray-700">
            <Button
              color="primary-destructive"
              type="button"
              onPress={() => setShowDeleteDialog(true)}
              iconLeading={Trash01Icon}
            >
              {t('resources.customers.delete_title')}
            </Button>

            <div className="flex items-center gap-3">
              <Button color="secondary" type="button" onPress={() => navigate('/customers')}>
                {t('resources.generic.cancel')}
              </Button>
              <Button
                color="primary"
                type="submit"
                isDisabled={isSubmitting || !isDirty}
                iconLeading={CheckIcon}
              >
                {isSubmitting ? t('resources.generic.loading') : t('resources.generic.save')}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('resources.customers.delete_title')}
        description={t('resources.customers.delete_description', { name: customer.name })}
        confirmLabel={t('resources.generic.delete')}
        variant="error"
        isLoading={deleteCustomer.isPending}
      />
    </div>
  )
}

export default CustomerEditPage
